// @flow

import axios from 'axios';
import { takeEvery, call, put } from 'redux-saga/effects';

import { getHeaders } from '@services/httpHeaders';
import { mainApiUrl } from '@lib';
import { Router } from '@i18n';
import { saveFormValues } from '@lib/flowConstant';

import type { NextCtx } from '@globalTypes';
import {
  CONSULTATION_PATIENT_FLOW, OPINION_PATIENT_FLOW, PACKAGE_PAYMENT, TREATMENT_PLAN_PATIENT_FLOW
} from '@routes';
import type { Action, CandidateCreateData, CandidateCreateResponse } from './types';

import {
  failCandidateCreate,
  successBookingCreate,
  successBookingUpdate,
  successCandidateCreate,
  successModalCandidateCreate,
  updateBookingDetails
} from './actions';
import {
  CANDIDATE_CREATE_REQUEST,
  PATIENT_SECOND_OPINION_CREATE_REQUEST,
  PATIENT_SECOND_OPINION_UPDATE_REQUEST,
  PATIENT_SECOND_OPINION_BOOKING_REQUEST
} from './constants';

export const getData = ({
  email,
  first_name,
  modalState,
  last_name,
  address_city,
  password,
  phone,
  phone_code,
  language,
  package_name,
  slug,
  notes,
  hours,
  location,
  treatment,
  doctor,
  hospital,
  speciality,
  departments,
  country = '',
  gender = '',
  source = '' // Should be the browser's query s param
}: CandidateCreateData): {} => ({
  gender,
  modalState,
  email,
  phone,
  first_name,
  last_name,
  address_city,
  password,
  phone_code,
  address_country: country,
  flow_data: {
    answers: {
      illness_type: hours,
      package_slug: slug,
      notes: package_name || notes,
      treatment_type: location,
      treatment_page: treatment,
      doctor_page: doctor,
      hospital_page: hospital,
      doctor_speciality: speciality,
      department: departments
    },
    language,
    variant: 'a',
    client: 'web',
    source,
    country
  }
});

export const getOpinionData = ({
  first_name,
  last_name,
  address_city,
  phone,
  phone_code,
  language,
  doctor_id,
  consult,
  doctor,
  department,
  country = '',
  booking_currency = 'INR',
  booking_category = 'second-opinion',
  source = 'Website'
}: CandidateCreateData): {} => ({
  phone,
  first_name,
  last_name,
  address_city,
  doctor_id,
  consult,
  phone_code,
  address_country: country,
  flow_data: {
    answers: {
      booking_slug: department,
      booking_category,
      booking_currency,
      doctor_page: doctor
    },
    language,
    variant: 'a',
    client: 'web',
    source,
    country
  }
});

export function*
opinionPatientCreate(nextCtx: NextCtx, action: Action): Generator<Function, void, any> {
  try {
    const data: { modalState: boolean } = yield call(getOpinionData, action.data);
    const {
      department, booking_category, doctor_id, avatarUser, consult
    } = action.data;

    // eslint-disable-next-line no-unused-vars
    const patientCreateResponse: CandidateCreateResponse = yield call(
      axios.post,
      `${mainApiUrl}/v1/patient_bookings/`,
      data,
      { headers: getHeaders(nextCtx) }
    );

    const patientId = patientCreateResponse?.data?.data?.attributes?.patient_id;
    const bookingId = patientCreateResponse?.data?.data?.id;

    const params = {
      consult,
      department,
      patientId,
      bookingId,
      avatarUser,
      doctor_id
    };

    Object.keys(params).forEach(key =>
      params[key] === undefined && delete params[key]);

    yield call(Router.pushRoute,
      booking_category === 'online-consultation' || doctor_id
        ? CONSULTATION_PATIENT_FLOW : OPINION_PATIENT_FLOW, { path: 'gender', ...params });

    saveFormValues(`OpinionForm-${patientId || ''}`, {});

    yield put(successBookingCreate(patientCreateResponse));
  } catch (error) {
    yield put(failCandidateCreate(error));
  }
}

export function*
opinionPatientUpdate(nextCtx: NextCtx, action: Action): Generator<Function, void, any> {
  try {
    // eslint-disable-next-line no-unused-vars
    const patientUpdateResponse: CandidateCreateResponse = yield call(
      axios.post,
      `${mainApiUrl}/v1/patient_bookings/update_booking`,
      {
        patient_booking: action.data,
        patient_id: action.data.patientId,
        patient_booking_id: action.data.patientBookingId,
        category: action.data.category,
        use_last_booking: action.data.use_last_booking,
        department: action.data.department,
      },
      { headers: getHeaders(nextCtx) }
    );
    if (action.data.category === 'treatment-plan') {
      const params = {
        patientId: action.data.patientId,
        avatarUser: action.data.avatarUser,
        bookingId: patientUpdateResponse?.data?.data?.id,
        department: action.data.department,
        treatments: action.data.treatments,
        preferred_country: action.data.preferred_country
      };
      yield call(Router.pushRoute,
        TREATMENT_PLAN_PATIENT_FLOW, { ...params, path: 'documents' });
    }
    yield put(successBookingUpdate(patientUpdateResponse));
  } catch (error) {
    yield put(failCandidateCreate(error));
  }
}

export function* getOpinionBookingFlow({ slug }: Action): Generator<Function, void, void> {
  if (!slug) return;
  try {
    const response: ?CandidateCreateResponse = yield call(axios.get,
      `${mainApiUrl}/v1/patient_bookings/${slug}`);

    if (!response) throw new Error();

    yield put(updateBookingDetails(response));
  } catch (error) {
    yield put(failCandidateCreate(error));
  }
}

export function* candidateCreate(nextCtx: NextCtx, action: Action): Generator<Function, void, any> {
  try {
    const data: { modalState: boolean } = yield call(getData, action.data);
    const { modalState } = data;
    const { doctor, hospital, contactUs } = action.data;

    // eslint-disable-next-line no-unused-vars
    const candidateCreateResponse: CandidateCreateResponse = yield call(
      axios.post,
      `${mainApiUrl}/v1/patients/`,
      data,
      { headers: getHeaders(nextCtx) }
    );
    if (modalState) {
      yield put(successModalCandidateCreate(candidateCreateResponse));
    } else if (doctor || hospital || contactUs) {
      yield put(successCandidateCreate(candidateCreateResponse));
    } else if (action?.data?.slug) {
      yield put(successCandidateCreate(candidateCreateResponse));
      yield call(Router.pushRoute, PACKAGE_PAYMENT, {
        slug: action?.data?.slug,
        patientId: candidateCreateResponse?.data?.patient_id,
        hotel: 'true'
      });
    } else {
      const patientId = candidateCreateResponse?.data?.patient_id;
      const {
        avatarUser,
        department
      } = action.data;

      const params = {
        patientId,
        avatarUser,
        department
      };

      Object.keys(params).forEach(key =>
        params[key] === undefined && delete params[key]);

      yield call(Router.pushRoute,
        TREATMENT_PLAN_PATIENT_FLOW, { ...params, path: department ? 'treatment' : 'department' });

      saveFormValues(`TreatmentCostForm-${patientId || ''}`, {});

      yield put(successCandidateCreate(candidateCreateResponse));
    }
  } catch (error) {
    yield put(failCandidateCreate(error));
  }
}

export function* candidateCreateFlow(
  nextCtx: NextCtx,
  action: Action
): Generator<Function, void, Action> {
  switch (action.type) {
    case CANDIDATE_CREATE_REQUEST:
      yield call(candidateCreate, nextCtx, action);
      break;
    case PATIENT_SECOND_OPINION_CREATE_REQUEST:
      yield call(opinionPatientCreate, nextCtx, action);
      break;
    case PATIENT_SECOND_OPINION_UPDATE_REQUEST:
      yield call(opinionPatientUpdate, nextCtx, action);
      break;
    case PATIENT_SECOND_OPINION_BOOKING_REQUEST:
      yield call(getOpinionBookingFlow, action);
      break;
    default:
      break;
  }
}

export function* candidateCreateWatcher(nextCtx: NextCtx): Generator<Function, void, Action> {
  yield takeEvery(
    [
      CANDIDATE_CREATE_REQUEST,
      PATIENT_SECOND_OPINION_CREATE_REQUEST,
      PATIENT_SECOND_OPINION_UPDATE_REQUEST,
      PATIENT_SECOND_OPINION_BOOKING_REQUEST,
    ],
    candidateCreateFlow, nextCtx
  );
}

export default candidateCreateWatcher;
